import PaymentMethod from '@alltrails/analytics/enums/PaymentMethod';
import WebProPurchaseLocation from '@alltrails/analytics/enums/WebProPurchaseLocation';
import proApi from 'api/Pro';
import useLanguageRegionCode from 'hooks/useLanguageRegionCode';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import Coupon from 'types/Coupon';
import CurrencyCode from 'types/CurrencyCode';
import Plan from 'types/Plan';
import logError from 'utils/logError';
import { handlePaymentFormSuccess } from 'utils/Payment';
import { execute } from 'utils/recaptcha_helpers';
import Typography from 'components/Typography';
import ProResponse from 'types/ProResponse';
import { ApplePayInstance } from '@recurly/recurly-js';
import { getPriceTotal } from 'utils/PlanUtils';
import * as styles from './ApplePayButton.module.scss';

type Props = {
  coupon?: Coupon;
  currencyCode: CurrencyCode;
  plan: Plan;
  recurlyPublicKey: string;
  setErrors: (errors: ProResponse['errors']) => void;
  userId: number;
};

// Desktop: https://app.zeplin.io/project/627c18d6c6189e2a52ce6566/screen/627c1bcf26ba802db57c180b
// Mobile: https://app.zeplin.io/project/627c18d6c6189e2a52ce6566/screen/627c241f824169281f996dc1
export default function ApplePayButton({ coupon, currencyCode, plan, recurlyPublicKey, userId, setErrors }: Props) {
  const languageRegionCode = useLanguageRegionCode();
  const [applePayInstance, setApplePayInstance] = useState<ApplePayInstance>();
  const [applePayReady, setApplePayReady] = useState(false);

  useEffect(() => {
    // https://github.com/recurly/recurly-js/blob/4e2789fd7e5a8e07e8dcd5a204a25ca92894ba68/lib/recurly/apple-pay/apple-pay.js#L57
    if (window.ApplePaySession && window.ApplePaySession.canMakePayments()) {
      recurly.configure(recurlyPublicKey);

      const applePay = recurly.ApplePay({
        country: 'US', // this is the merchant's country code, so will remain US
        currency: currencyCode,
        label: 'AllTrails Pro',
        total: String(getPriceTotal(plan, coupon))
      });

      applePay.on('error', error => {
        logError(error);
      });

      applePay.on('token', async token => {
        const recaptcha = await execute('userPaymentAction');

        const body = {
          plan: plan.planCode,
          recurly_plan_code: plan.recurlyPlanCode,
          apple_pay_token: token.id,
          recaptcha,
          uid: userId,
          user: {
            user_entered_coupon_code: coupon ? coupon.code : null
          }
        };

        try {
          const response = await proApi(body);

          if (response?.errors) {
            setErrors(response.errors);
          } else {
            handlePaymentFormSuccess(
              currencyCode,
              coupon,
              plan,
              languageRegionCode,
              coupon?.code,
              WebProPurchaseLocation.ProPage,
              PaymentMethod.ApplePay
            );
          }
        } catch (e) {
          setErrors(e);
        }
      });

      applePay.ready(() => {
        setApplePayReady(true);
      });

      setApplePayInstance(applePay);
    }
  }, [coupon, currencyCode, languageRegionCode, plan, recurlyPublicKey, setErrors, userId]);

  return (
    <button
      className={styles.button}
      onClick={() => {
        if (applePayReady) {
          applePayInstance.begin();
        }
      }}
      type="button"
    >
      <Typography variant="headline2">
        <FormattedMessage defaultMessage="Continue with" />
      </Typography>
      &nbsp;
      <span className={styles.logo} />
    </button>
  );
}
